import React from "react";
import {observer} from "mobx-react-lite";
import {Alert, Button} from "@mui/material";
import {AutoAwesome} from "@mui/icons-material";
import {useSitStores} from "~/stores";
import type {BaselineSource, InitiativeFinancial} from "../../../ApiTypes";
import {BaselineSourceValues} from "../../../ApiTypes";
import {baselineSourceLabel, sitColumnLabel, sitFieldLabel} from "../../../sitLabels";
import {SitField, SitRead} from "../../../components/design/SitDesignAtoms";
import {roundAmount} from "~/utils/js-utils";
import i18n from "../../../../i18n/i18n";

// Text money inputs: parse to float and round so the stored value (and the value
// sent to the backend) is exactly what the user sees, free of float noise.
const num = (e: React.ChangeEvent<HTMLInputElement>) => roundAmount(parseFloat(e.target.value));
const fmt = (v: number | null | undefined) => (Number(v) || 0).toLocaleString();

export const InitiativeFinancialForm: React.FC = observer(() => {
    const {ccStore, initiativeInstanceStore: s} = useSitStores();
    const fc = s.f_c[0];
    // In an amount-mode split, the primary is the read-only Σ of the participants'
    // financials (edited in the Saving split section), so its inputs are disabled.
    const amountSplit = s.f_c.length > 1 && s.splitMode === 'amount';
    const dis = s.disabled || amountSplit;
    // Prefix the unit with "K" when amounts are shown in thousands (see SitConfiguration).
    const ccy = ccStore.getCurrencyCode(fc.instance.currency_id, ccStore.valuesInThousands ? 'K ' : '');
    const kHelp = ccStore.valuesInThousands ? 'Value in thousands (k).' : undefined;
    const sc = fc.scenario;
    const noRate = ccStore.convertToCurrency(1, fc.instance.currency_id, s.profileStore.currencySymbol ?? '') === 0;

    // The design tracks a single primary type (cost saving OR cost avoidance).
    // We keep both underlying amounts; the toggle just picks which Total is edited.
    const [type, setType] = React.useState<'saving' | 'avoidance'>(
        (fc.instance.avoidance_amount ?? 0) > 0 && !((fc.instance.saving_amount ?? 0) > 0) ? 'avoidance' : 'saving');
    const isAvoid = type === 'avoidance';
    const typeLabel = (isAvoid ? sitColumnLabel('costAvoidance') : sitColumnLabel('costSavings')).toLowerCase();

    const total = isAvoid ? fc.instance.avoidance_amount : fc.instance.saving_amount;
    const setTotal = (value: number) => {
        if (isAvoid) {
            fc.avoidanceAmount = value;
            fc.fillAvoidances(true);
        } else {
            fc.savingAmount = value;
            fc.fillSavings(true);
        }
    };
    // Read a breakdown field for the active type.
    const rd = (savingKey: keyof InitiativeFinancial, avoidKey: keyof InitiativeFinancial) =>
        fmt(fc.instance[isAvoid ? avoidKey : savingKey] as number);
    const pct = isAvoid ? fc.instance.avoidance_percentage : fc.instance.saving_percentage;

    return (
        <>
            {amountSplit && <Alert severity="info" className="mb-2">
                These totals are the sum of the saving-split participants. Edit the amounts in the “Saving split”
                section.
            </Alert>}
            <div className="sit-grid">
                <SitField label="Currency" req>
                    <select required value={fc.instance.currency_id || ''} disabled={dis}
                            onChange={e => {
                                if (e.target.value) fc.currencyId = Number(e.target.value);
                            }}>
                        <option value="">—</option>
                        {ccStore.currencyRM.result?.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
                    </select>
                </SitField>
                <SitField label="Baseline spend (BP)" units={ccy}
                          help={"Current spend in scope. Drives the efficiency % and the reports. " + (kHelp ?? '')}>
                    <input type="text" inputMode="decimal" disabled={dis} value={fc.instance.baseline_spend ?? 0}
                           onChange={e => fc.baselineSpend = num(e)}/>
                </SitField>
                <SitField label={sitFieldLabel('baselineSource')}>
                    <select value={fc.instance.baseline_source ?? ''} disabled={dis}
                            onChange={e => fc.baselineSource = (e.target.value ? Number(e.target.value) : null) as BaselineSource | null}>
                        <option value="">—</option>
                        {BaselineSourceValues.map(v => <option key={v} value={v}>{baselineSourceLabel(v)}</option>)}
                    </select>
                </SitField>
            </div>

            {noRate && <Alert severity="error" color="error" className="mt-2">
                Conversion rate not found! Add a rate for the selected currency pair in settings.
            </Alert>}

            {/* Negotiation & two-scenario price split (Playbook §4.2) */}
            <div className="sit-px">
                <div className="sit-px-head">
                    <span>{i18n.t('section.negotiation', {ns: 'sit'})}</span>
                </div>
                <div className="sit-px-body">
                    <div className="sit-grid">
                        <SitField label={sitFieldLabel('bpValue')} units={ccy} help={'Business plan ' + kHelp}>
                            <input type="text" inputMode="decimal" disabled={dis} value={fc.instance.bp_value ?? 0}
                                   onChange={e => fc.bpValue = num(e)}/>
                        </SitField>
                        <SitField label={sitFieldLabel('baselineEvolved')} units={ccy}>
                            <input type="text" inputMode="decimal" disabled={dis}
                                   value={fc.instance.baseline_evolved ?? 0}
                                   onChange={e => fc.baselineEvolved = num(e)}/>
                        </SitField>
                        <SitField label={sitFieldLabel('firstProposal')} units={ccy} help={kHelp}>
                            <input type="text" inputMode="decimal" disabled={dis}
                                   value={fc.instance.first_proposal ?? 0}
                                   onChange={e => fc.firstProposal = num(e)}/>
                        </SitField>
                        <SitField label={sitFieldLabel('negotiatedValue')} units={ccy} help={kHelp}>
                            <input type="text" inputMode="decimal" disabled={dis}
                                   value={fc.instance.negotiated_value ?? 0}
                                   onChange={e => fc.negotiatedValue = num(e)}/>
                        </SitField>
                        <SitField label={sitFieldLabel('volume')}>
                            <input type="text" inputMode="decimal" disabled={dis} value={fc.instance.volume ?? 0}
                                   onChange={e => fc.volume = num(e)}/>
                        </SitField>
                    </div>
                    {sc.scenario === null
                        ? <Alert severity="info" className="mt-2">{i18n.t('scenario.none', {ns: 'sit'})}</Alert>
                        : <div className="sit-scn">
                            <div className={'sit-scn-badge scn-' + sc.scenario}>
                                {i18n.t(`scenario.${sc.scenario}`, {ns: 'sit'})}
                            </div>
                            <div className="sit-scn-split">
                                <div className="sit-scn-cell good">
                                    <span>{sitColumnLabel('costSavings')}</span><strong>{sc.saving.toLocaleString()}</strong>
                                </div>
                                <div className="sit-scn-cell violet">
                                    <span>{sitColumnLabel('costAvoidance')}</span><strong>{sc.avoidance.toLocaleString()}</strong>
                                </div>
                            </div>
                            <Button size="small" disabled={dis} onClick={() => fc.applyScenario()}>
                                {i18n.t('scenario.apply', {ns: 'sit'})}
                            </Button>
                        </div>}
                </div>
            </div>

            {/* Type toggle (cost savings / cost avoidance) + single total + auto-calc */}
            <div className="sit-type-toggle" style={{marginTop: 14}}>
                <button type="button" className={isAvoid ? '' : 'on'} disabled={dis} onClick={() => setType('saving')}>
                    {sitColumnLabel('costSavings')}
                </button>
                <button type="button" className={isAvoid ? 'on' : ''} disabled={dis}
                        onClick={() => setType('avoidance')}>
                    {sitColumnLabel('costAvoidance')}
                </button>
            </div>

            <div className="sit-grid" style={{marginTop: 14}}>
                <SitField label={'Total ' + typeLabel} req units={ccy} help={kHelp}>
                    <input type="text" inputMode="decimal" disabled={dis} value={total ?? 0}
                           onChange={e => setTotal(num(e))}/>
                </SitField>
                <SitField label={sitFieldLabel('estimatedEfficiency')} units={ccy}
                          help={"Auditable basis of the calculated saving. BP − negotiated value is never substituted automatically. " + (kHelp ?? '')}>
                    <input type="text" inputMode="decimal" disabled={dis}
                           value={fc.instance.estimated_efficiency ?? 0}
                           onChange={e => fc.estimatedEfficiency = num(e)}/>
                </SitField>
            </div>

            <div className="sit-autocalc">
                <div className="sit-autocalc-head">
                    <AutoAwesome fontSize="inherit"/> Auto-calculated on entry — fiscal &amp; running logic ({ccy})
                </div>
                <div className="sit-autocalc-grid">
                    <SitRead label={'Total ' + typeLabel} tone="strong" value={fmt(total)}/>
                    <SitRead label={'Rolling-year ' + typeLabel}
                             value={rd('normal_year_saving_amount', 'normal_year_avoidance_amount')}/>
                    <SitRead label={'Fiscal-year ' + typeLabel}
                             value={rd('fiscal_year_saving_amount', 'fiscal_year_avoidance_amount')}/>
                    <SitRead label="Carry-over (12-month)"
                             value={rd('normal_carry_over_saving_amount', 'normal_carry_over_avoidance_amount')}/>
                    <SitRead label="Carry-over (fiscal)"
                             value={rd('fiscal_carry_over_saving_amount', 'fiscal_carry_over_avoidance_amount')}/>
                    <SitRead label="% on baseline" value={(Number(pct) || 0).toFixed(1) + '%'}/>
                </div>
            </div>

            <div className="sit-grid" style={{marginTop: 14}}>
                <SitField label="Required investment" units={ccy}
                          help="One-off spend to realize the saving; deducted from saving tracking.">
                    <input type="text" inputMode="decimal" disabled={dis} value={fc.instance.investment ?? 0}
                           onChange={e => fc.investment = num(e)}/>
                </SitField>
                <SitField label="Required severance" units={ccy}
                          help="Penalties paid to realize the saving; deducted from saving tracking.">
                    <input type="text" inputMode="decimal" disabled={dis} value={fc.instance.severance ?? 0}
                           onChange={e => fc.severance = num(e)}/>
                </SitField>
            </div>
        </>
    );
});
